import React from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Activity,
  AlertTriangle,
  XCircle,
  CheckCircle,
  BellRing,
  History,
  ShieldAlert,
  Check,
  Loader2
} from 'lucide-react';

const MonitoringAlerts = ({
  activeAlerts,
  alertHistory,
  checkingAlerts,
  onManualAlertCheck,
  onAcknowledgeAlert,
  getSeverityConfig
}) => {
  return (
    <div className="space-y-6">
      {/* Alert Actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">System Alerts</h2>
        <Button
          onClick={onManualAlertCheck}
          disabled={checkingAlerts}
          className="bg-pink-600 hover:bg-pink-700"
        >
          {checkingAlerts ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <ShieldAlert className="h-4 w-4 mr-2" />
          )}
          Run Alert Check
        </Button>
      </div>

      {/* Active Alerts */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <BellRing className="h-5 w-5 text-red-600" />
          <h3 className="font-semibold">Active Alerts ({activeAlerts.length})</h3>
        </div>
        
        {activeAlerts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
            <p>No active alerts. All systems operating normally.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeAlerts.map(alert => {
              const config = getSeverityConfig(alert.severity);
              const Icon = config.icon;
              return (
                <div key={alert.id} className={`p-4 rounded-lg border ${config.color} ${config.bgColor}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Icon className="h-5 w-5 mt-0.5" />
                      <div>
                        <p className="font-medium">{alert.message}</p>
                        <p className="text-sm opacity-75">
                          {alert.type.replace(/_/g, ' ').toUpperCase()} • {new Date(alert.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAcknowledgeAlert(alert.id)}
                      className="shrink-0"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Acknowledge
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Alert History */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <History className="h-5 w-5 text-gray-600" />
          <h3 className="font-semibold">Alert History</h3>
        </div>
        
        {alertHistory.length === 0 ? (
          <p className="text-center py-4 text-gray-500">No alert history</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {alertHistory.map((alert, idx) => {
              const config = getSeverityConfig(alert.severity);
              const Icon = config.icon;
              return (
                <div key={alert.id || idx} className={`p-3 rounded-lg border flex items-center justify-between ${alert.acknowledged ? 'bg-gray-50 opacity-60' : config.bgColor}`}>
                  <div className="flex items-center gap-3">
                    <Icon className={`h-4 w-4 ${alert.acknowledged ? 'text-gray-400' : ''}`} />
                    <div>
                      <p className={`text-sm ${alert.acknowledged ? 'text-gray-500' : ''}`}>{alert.message}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(alert.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {alert.acknowledged && (
                    <Badge className="bg-gray-200 text-gray-600 text-xs">Acknowledged</Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default MonitoringAlerts;
